import { useCallback, useEffect, useRef, useState } from 'react';
import { catalogApiRef } from '@backstage/plugin-catalog-react';
import { useApi } from '@backstage/core-plugin-api';
import { Entity } from '@backstage/catalog-model';
import { CATALOG_FILTER_EXISTS } from '@backstage/catalog-client';
import { Flex, Text } from '@backstage/ui';
import { CardVariant, TechDocsCard } from '../../../ui/card';
import { featuredTechDocs } from '@internal/backstage-plugin-hm-common';
import styles from './featured-techdocs.module.css';

const FEATURED_TECH_DOCS_VISIBLE_COUNT = 3;

type FeaturedTechDocsProps = {
  maxHeight?: string;
};

const shuffleEntities = (entities: Entity[]): Entity[] => {
  const shuffled = [...entities];

  for (let index = shuffled.length - 1; index > 0; index -= 1) {
    const randomBuffer = new Uint32Array(1);
    crypto.getRandomValues(randomBuffer);
    const randomIndex = randomBuffer[0] % (index + 1);
    [shuffled[index], shuffled[randomIndex]] = [
      shuffled[randomIndex],
      shuffled[index],
    ];
  }

  return shuffled;
};

export const FeaturedTechDocs = ({ maxHeight }: FeaturedTechDocsProps) => {
  const catalogApi = useApi(catalogApiRef);
  const [featured, setFeatured] = useState<Entity[]>([]);
  const scrollRef = useRef<HTMLDivElement>(null);
  const [showFade, setShowFade] = useState(false);

  const updateFade = useCallback(() => {
    const el = scrollRef.current;
    if (!el) return;
    const isOverflowing = el.scrollHeight > el.clientHeight;
    const isAtBottom = el.scrollTop + el.clientHeight >= el.scrollHeight - 1;
    setShowFade(isOverflowing && !isAtBottom);
  }, []);

  useEffect(() => {
    const fetchEntities = async () => {
      const { items } = await catalogApi.getEntities({
        filter: {
          'metadata.annotations.backstage.io/techdocs-ref':
            CATALOG_FILTER_EXISTS,
          'metadata.name': featuredTechDocs
            .filter(doc => doc.onHomepage)
            .map(doc => doc.name),
        },
      });
      setFeatured(
        shuffleEntities(items).slice(0, FEATURED_TECH_DOCS_VISIBLE_COUNT),
      );
    };
    fetchEntities();
  }, [catalogApi]);

  useEffect(() => {
    updateFade();
  }, [featured, updateFade, maxHeight]);

  return (
    <div className={styles.wrapper}>
      <div
        ref={scrollRef}
        className={styles.scrollContainer}
        style={{
          maxHeight,
          overflowY: 'auto',
          paddingRight: 'var(--bui-space-1)',
        }}
        onScroll={updateFade}
      >
        <Flex direction="column" gap="4">
          {featured.length === 0 && (
            <Text key="no-featured-techdocs">No featured TechDocs to show</Text>
          )}

          {featured.map(entity => (
            <TechDocsCard
              key={entity.metadata.uid}
              actionable
              withoutTheme
              entity={entity}
              variant={CardVariant.minimal}
            />
          ))}
        </Flex>
      </div>
      {showFade && <div className={styles.fade} aria-hidden="true" />}
    </div>
  );
};
